import * as XLSX from 'xlsx';

interface ParsedRecord {
  county: string;
  documentNumber: string;
  date: string;
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export const parseSellerName = (rawNames: string): { firstName: string; lastName: string } => {
  // Handle complex name formats like "SMITH MICHAEL-MEMBER" or "JONES JOHN C-TR"
  const names = rawNames.split(';')[0].trim(); // Take first name only
  
  // Remove suffixes and roles
  const cleanName = names
    .replace(/-MEMBER|-TR|-AS EXEC|-JR|-SR|-II/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  const parts = cleanName.split(/\s+/);
  
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  
  // Handle LLC/TRUST/INC names
  if (cleanName.includes(' L L C') || cleanName.includes(' LLC') || 
      cleanName.includes(' TRUST') || cleanName.includes(' INC') ||
      cleanName.includes(' LTD') || cleanName.includes(' PROPERTIES')) {
    return { firstName: cleanName, lastName: '' };
  }
  
  // Remove middle initials (single letters)
  const filteredParts = parts.filter(part => part.length > 1 || parts.length === 2);
  
  if (filteredParts.length === 1) {
    return { firstName: filteredParts[0], lastName: '' };
  }
  
  // Last name first, first name last format
  const lastName = filteredParts[0];
  const firstName = filteredParts.slice(1).join(' ');
  
  return { firstName, lastName };
};

const STATE_ABBREVIATIONS: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

export const parseAddress = (rawAddress: string): { street: string; city: string; state: string; zip: string } => {
  if (!rawAddress || rawAddress.trim() === '') {
    return { street: '', city: '', state: '', zip: '' };
  }
  
  // Clean up the address - remove extra text like "County of Summit, State of Ohio"
  let cleanAddress = rawAddress
    .replace(/,?\s*County of [^,]+/gi, '')
    .replace(/,?\s*State of [^,]+/gi, '')
    .replace(/\s*\(during the months[^)]*\)/gi, '')
    .trim();
  
  const parts = cleanAddress.split(',').map(p => p.trim());
  
  if (parts.length < 2) {
    return { street: cleanAddress, city: '', state: '', zip: '' };
  }
  
  const street = parts[0];
  let state = '';
  let zip = '';
  let city = '';
  
  // Last part typically contains state and/or zip
  const lastPart = parts[parts.length - 1];
  
  // Try to match "State ZIP" or "ST ZIP" pattern
  const stateZipMatch = lastPart.match(/([A-Z][a-z]+|[A-Z]{2})\s+(\d{5}(-\d{4})?)/);
  
  if (stateZipMatch) {
    const stateRaw = stateZipMatch[1];
    state = STATE_ABBREVIATIONS[stateRaw] || stateRaw;
    zip = stateZipMatch[2].split('-')[0]; // Get just the 5-digit zip
    
    // City is everything between street and the last part
    if (parts.length === 3) {
      city = parts[1];
    } else if (parts.length > 3) {
      city = parts.slice(1, -1).join(', ');
    } else {
      // City might be in the same part as state/zip
      city = lastPart.replace(stateZipMatch[0], '').trim();
    }
  } else {
    // No zip found, try to find just state
    const stateOnlyMatch = lastPart.match(/\b([A-Z][a-z]+|[A-Z]{2})\s*$/);
    
    if (stateOnlyMatch) {
      const stateRaw = stateOnlyMatch[1];
      state = STATE_ABBREVIATIONS[stateRaw] || stateRaw;
      
      if (parts.length === 3) {
        city = parts[1];
      } else if (parts.length > 3) {
        city = parts.slice(1, -1).join(', ');
      } else {
        city = lastPart.replace(stateOnlyMatch[0], '').trim();
      }
    } else if (parts.length === 2) {
      // Just street and city
      city = parts[1];
    } else {
      // Multiple parts but no clear state
      city = parts.slice(1).join(', ');
    }
  }
  
  return { 
    street: street.trim(), 
    city: city.trim(), 
    state: state.trim(), 
    zip: zip.trim() 
  };
};

export const processCountyData = (rawData: string): ParsedRecord[] => {
  const lines = rawData.trim().split('\n').filter(line => line.trim() !== '');
  const records: ParsedRecord[] = [];
  const seen = new Set<string>();
  
  for (const line of lines) {
    const parts = line.split('\t');
    
    if (parts.length < 4) continue;
    
    const county = parts[0].trim();
    const documentNumber = parts[1].trim();
    const date = parts[2].trim();
    const rawNames = parts[3].trim();
    const rawAddress = parts[4]?.trim() || '';
    
    // Skip duplicate Dickinson entries (they're mislabeled Licking records)
    const uniqueKey = `${documentNumber}-${date}`;
    if (seen.has(uniqueKey)) continue;
    seen.add(uniqueKey);
    
    // Skip Dickinson entirely as they're duplicates
    if (county.toLowerCase() === 'dickinson') continue;
    
    const { firstName, lastName } = parseSellerName(rawNames);
    const { street, city, state, zip } = parseAddress(rawAddress);
    
    records.push({
      county,
      documentNumber,
      date,
      firstName,
      lastName,
      street,
      city,
      state,
      zip
    });
  }
  
  return records;
};

export const exportToExcel = (records: ParsedRecord[], filename: string = 'county_records.xlsx'): void => {
  const worksheet = XLSX.utils.json_to_sheet(records.map(r => ({
    'County': r.county,
    'Document Number': r.documentNumber,
    'Date': r.date,
    'First Name': r.firstName,
    'Last Name': r.lastName,
    'Street': r.street,
    'City': r.city,
    'State': r.state,
    'ZIP': r.zip
  })));
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 12 },  // County
    { wch: 18 },  // Document Number
    { wch: 12 },  // Date
    { wch: 25 },  // First Name
    { wch: 20 },  // Last Name
    { wch: 35 },  // Street
    { wch: 20 },  // City
    { wch: 8 },   // State
    { wch: 10 }   // ZIP
  ];
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'County Records');
  
  XLSX.writeFile(workbook, filename);
};
